import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import isDatepickerSupport from "scripts/utils/isDatepickerSupport"
import isMobileDevice from "scripts/utils/isMobileDevice"

/**
 * @param {boolean} isError - error state
 * @param {boolean} focus - focus state (if true, then the input is focused and the label is raised (by adding the class "focus" to the input and the label))
 * @param {string} title - label text
 * @param {string} note - note text (under the input)
 * @param {string} error - error text (under the input)
 * @param {React.ReactNode} children - children
 */
export const Input = React.forwardRef(
  (
    {
      isError,
      focus,
      title,
      note,
      error,
      children,
      //  "data-country": dataCountry,
      ...restProps
    },
    ref,
  ) => (
    <label
      className={`form-label ${isError ? "error" : ""} form-label_${restProps.name}`}
    >
      {/* {restProps.type === "tel" && (
        <span className="form-label__prefix form-label__prefix_flag">
          <img src={`/images/flag/${dataCountry || "PHONE"}.svg`} alt={dataCountry} />
        </span>
      )} */}
      <input
        {...restProps}
        // type="text"
        className={`form-label__input ${focus ? "focus" : ""}`}
        ref={ref}
        // TODO: Delete after full implementation intl phone numbers
        // {...(restProps.type === "tel" ? { "data-intl": true } : {})}
      />
      <span className="form-label__placeholder">{title}</span>
      {children}
      {isError ? <span className="error-text">{error}</span> : ""}
      {note && <span className="form-label__text">{note}</span>}
    </label>
  ),
)
Input.displayName = "Input"

export const InputFile = React.forwardRef(
  ({ isError, title, note, error, children, watch, buttonTitle = "Select", ...restProps }, ref) => {
    const fileInputRef = useRef(null)
    const fileLabelRef = useRef(null)
    let fileName = ""

    if (typeof watch === "function") {
      fileName = watch(restProps.name, "")?.[0]?.name
    }

    return (
      <label
        className={`form-label ${isError ? "error" : ""} form-label_${restProps.name}`}
      >
        <input
          {...restProps}
          type="file"
          hidden
          ref={(e) => {
            ref(e)
            fileInputRef.current = e
          }}
        />
        <span
          className={`form-label__input form-label__input--file ${fileName ? "focus" : ""}`}
          ref={(e) => {
            fileLabelRef.current = e
          }}
        >
          {fileName}
        </span>
        <span className="form-label__placeholder">{title}</span>
        {children}
        {isError ? <span className="error-text">{error}</span> : ""}
        {note && <span className="form-label__text">{note}</span>}
        <button type="button" className="form-label__file-button wb-btn btn-white" onClick={() => fileInputRef.current.click()}>
          {buttonTitle}
        </button>
      </label>
    )
  },
)
InputFile.displayName = "InputFile"

export const InputDate = React.forwardRef(
  ({ isError, focus, title, note, error, children, setValue, ...restProps }, ref) => {
    const dateInputRef = useRef(null)
    const textInputRef = useRef(null)

    const dateInputChangedHandler = (e) => {
      const dateValue = e.target.value
      const textDate = dateValue ? new Date(dateValue).toLocaleDateString() : ""
      textInputRef.current.value = textDate
      if (textDate) {
        textInputRef.current.setAttribute("value", textDate)
      } else {
        textInputRef.current.removeAttribute("value")
      }
      if (typeof setValue === "function") {
        setValue(restProps.name, dateValue)
      }
    }

    useEffect(() => {
      // dateInputChangedHandler({ target: dateInputRef.current })
      // dateInputRef.current.min = new Date().toISOString().split("T")[0]
      // dateInputRef.current.max = new Date(
      //   new Date().setFullYear(new Date().getFullYear() + 1),
      // )
      //   .toISOString()
      //   .split("T")[0]
    }, [])

    return (
      <div
        className={`form-label ${isError ? "error" : ""} form-label_${restProps.name}`}
      >
        <input
          {...restProps}
          className={`js_input_text_date form-label__input form-label__input_date ${
            focus ? "focus" : ""
          }`}
          name={`${restProps.name}_date-text`}
          id={restProps.id}
          readOnly
          ref={(e) => {
            // ref(e)
            textInputRef.current = e
          }}
          onClick={() => {
            textInputRef.current.dispatchEvent(new Event("focus", { bubbles: true }))
          }}
        />
        <label htmlFor={`${restProps.name}_full_id`} className="form-label__placeholder">
          {title}
        </label>
        {children}
        {isError ? <span className="error-text">{error}</span> : ""}
        {note && <span className="form-label__text">{note}</span>}
        <input
          ref={(e) => {
            ref(e)
            dateInputRef.current = e
          }}
          type="date"
          {...(restProps.min ? { min: restProps.min } : {})}
          {...(restProps.max ? { max: restProps.max } : {})}
          className={`js_input_full_date ${
            isMobileDevice() && isDatepickerSupport() ? "isMobile" : "text-hidden"
          }`}
          id={`${restProps.name}_full_id`}
          onChange={dateInputChangedHandler}
        />
      </div>
    )
  },
)
InputDate.displayName = "InputDate"

export const InputTime = React.forwardRef(
  ({ isError, title, note, error, children, setValue, name, ...restProps }, ref) => {
    const [time, setTime] = useState(null)

    const wrapperElRef = useRef(null)
    const inputTimeRef = useRef(null)
    const selectHourRef = useRef(null)
    const selectMinuteRef = useRef(null)

    const selectChangeHandler = (e) => {
      // const { name, value } = e.target

      // if (typeof setValue === "function") {
      //   setValue(name, value)
      // }
      setTime(`${selectHourRef.current.value}:${selectMinuteRef.current.value}`)
    }

    const timeInputChangedHandler = (e) => {
      let { value } = e.target
      if (value === "") value = restProps.defaultValue
      setTime(value)
      const [hour, minute] = value.split(":")
      selectHourRef.current.value = hour
      selectMinuteRef.current.value = minute
    }

    useLayoutEffect(() => {
      // Set value to custom select
      window.customSelect.update(`.js_${name}_select_time_hour`)
      window.customSelect.update(`.js_${name}_select_time_minute`)

      // Set value to parent form
      if (typeof setValue === "function" && time !== null) {
        setValue(name, time)
      }
    }, [time, name, setValue])

    return (
      <div
        className={`form-label ${isError ? "error" : ""} form-label_${name}`}
        ref={wrapperElRef}
      >
        <div className="time-inputs-wrapper">
          <select
            name={`${name}_hour`}
            className={`js_${name}_select_time_hour`}
            defaultValue={restProps.defaultValue?.split(":")[0] || "12"}
            onChange={selectChangeHandler}
            ref={selectHourRef}
          >
            {[...Array(24).keys()].map((item) => {
              const opt = item.toString().padStart(2, "0")
              return (
                <option value={opt} key={item}>
                  {opt}
                </option>
              )
            })}
          </select>
          <span>:</span>
          <select
            name={`${name}_minute`}
            className={`js_${name}_select_time_minute`}
            defaultValue={restProps.defaultValue?.split(":")[1] || "00"}
            onChange={selectChangeHandler}
            ref={selectMinuteRef}
          >
            {[...Array(60).keys()].map((item) => {
              const opt = item.toString().padStart(2, "0")
              return (
                <option value={opt} key={item}>
                  {opt}
                </option>
              )
            })}
          </select>
          <input
            type="time"
            className={`js_input_full_time ${
              isMobileDevice() && isDatepickerSupport() ? "isMobile" : "text-hidden"
            }`}
            ref={inputTimeRef}
            onClick={() => {
              try {
                inputTimeRef.current.showPicker()
              } catch (errorTimePicker) {
                console.log("Time picker error", errorTimePicker)
              }
            }}
            onChange={timeInputChangedHandler}
            defaultValue={restProps.defaultValue}
            data-time={time}
            data-default={restProps.defaultValue}
            required
          />
          {/* <input
          {...restProps}
          className={`form-label__input ${focus ? "focus" : ""}`}
          // ref={ref}
        /> */}
        </div>

        <span className="form-label__placeholder">{title}</span>
        {children}
        {isError ? <span className="error-text">{error}</span> : ""}
        {note && <span className="form-label__text">{note}</span>}
        {/* <input
          // ref={dateInputRef}
          type="date"
          className={`text-hidden js_input_full_date`}
          id={`${restProps.id}_full_date_id`}
          // onChange={dateInputChangedHandler}
        /> */}
      </div>
    )
  },
)
InputTime.displayName = "InputTime"

export const InputTextarea = React.forwardRef(
  ({ isError, focus, title, note, error, children, ...restProps }, ref) => {
    const textareaElRef = useRef(null)

    const onInput = (e) => {
      e.target.style.height = 0
      e.target.style.height = e.target.scrollHeight + "px"
    }

    useLayoutEffect(() => {
      setTimeout(() => {
        textareaElRef.current.setAttribute(
          "style",
          "height:" + textareaElRef.current.scrollHeight + "px;overflow-y:hidden;",
        )
        textareaElRef.current.addEventListener("input", onInput, false)
      }, 0)
    }, [textareaElRef])

    return (
      <label
        className={`form-label ${isError ? "error" : ""} form-label_${restProps.name}`}
      >
        <textarea
          {...restProps}
          className={`form-label__input ${focus ? "focus" : ""}`}
          ref={(e) => {
            ref(e)
            textareaElRef.current = e
          }}
        />
        <span className="form-label__placeholder">{title}</span>
        {children}
        {isError ? <span className="error-text">{error}</span> : ""}
        {note && <span className="form-label__text">{note}</span>}
      </label>
    )
  },
)
InputTextarea.displayName = "InputTextarea"
